"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useNinetailed, useProfile } from "@ninetailed/experience.js-react";
import { useConsent } from "../Consent/ConsentProvider";

// Stagger ladder for the React-side page() retries. Mirrors the one in
// ConsentProvider's syncNinetailedConsent — both paths share the same
// underlying SDK instance, so we keep the cadences slightly different
// to spread retries through the SET_ENABLED_FEATURES dispatch window
// without piling on top of each other. SDK-side asyncThrottle on
// _flush dedupes the underlying upsertProfile calls so the redundancy
// is cheap when one of them already returned audiences.
const PAGE_RETRY_DELAYS_MS = [200, 800, 2_000, 4_500];

// Stop firing follow-up page() calls once the API has returned a
// profile carrying an audiences array (even if empty). After that the
// SDK is fully caught up with consent and any further retries would
// just be redundant API hits.
function profileHasAudiences(profile: unknown): boolean {
  if (!profile || typeof profile !== "object") return false;
  const audiences = (profile as {
    audiences?: unknown;
  }).audiences;
  return Array.isArray(audiences);
}

/**
 * Lives inside <NinetailedProvider> and reacts to a fresh consent
 * grant by re-running ninetailed.page() until the profile arrives with
 * audiences attached — which is what causes <Experience> components
 * to swap from baseline to their personalized variants in place.
 *
 * Why this is separate from ConsentProvider:
 *  - ConsentProvider sits outside <NinetailedProvider> (so it can
 *    gate downstream tracking scripts), which means it can't use
 *    useNinetailed() / useProfile() and has to drive the SDK via
 *    window.ninetailed. That works for the common path, but it has
 *    no visibility into the actual profile state and so can't tell
 *    whether a given page() call resulted in audiences being loaded.
 *  - This component runs inside <NinetailedProvider>, so it can
 *    subscribe to profile state via useProfile() and short-circuit
 *    the retry loop the moment a profile with audiences shows up.
 *  - Belt-and-braces: even if ConsentProvider's window.ninetailed
 *    sync misses (e.g. it ran before the SDK was on window, lost a
 *    SET_ENABLED_FEATURES race, etc.), this component will still
 *    drive the experiences into their consented state.
 */
export default function NinetailedConsentSync() {
  const $ = _c(7);
  const {
    hasConsent
  } = useConsent();
  const ninetailed = useNinetailed();
  const profile = useProfile();
  const refreshStartedRef = useRef(false);
  const profileLoadedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== profile) {
    t0 = () => {
      if (profileLoadedRef.current) {
        return;
      }
      if (profileHasAudiences(profile.profile)) {
        profileLoadedRef.current = true;
      }
    };
    t1 = [profile];
    $[0] = profile;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== hasConsent || $[4] !== ninetailed) {
    t2 = () => {
      if (!hasConsent) {
        return;
      }
      if (refreshStartedRef.current) {
        return;
      }
      refreshStartedRef.current = true;
      let cancelled = false;
      const timers = [];
      const firePage = async () => {
        if (cancelled) {
          return;
        }
        if (profileLoadedRef.current) {
          return;
        }
        ;
        try {
          await ninetailed.page();
        } catch (t4) {
          const error = t4;
          console.warn("[Ninetailed] consent-sync page() failed:", error);
        }
      };
      firePage();
      for (const delay of PAGE_RETRY_DELAYS_MS) {
        timers.push(setTimeout(firePage, delay));
      }
      return () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      };
    };
    t3 = [hasConsent, ninetailed];
    $[3] = hasConsent;
    $[4] = ninetailed;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  return null;
}